/**
 * Helper functions for success page
 * Includes SHA256 hashing for Snap Pixel and other tracking pixels
 */

/**
 * Normalizes and hashes email using SHA256 for pixel tracking
 * Following Snap and Facebook's requirements:
 * - Convert to lowercase
 * - Remove all whitespace
 * - Hash with SHA256
 */
export async function hashEmail(email: string): Promise<string | null> {
  if (!email) return null;

  try {
    // Normalize: lowercase and remove all whitespace
    const normalized = email.toLowerCase().replace(/\s+/g, '');

    // Convert string to ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);

    // Hash using SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    console.error('Error hashing email:', error);
    return null;
  }
}

/**
 * Normalizes and hashes phone number using SHA256
 * Following Snap and Facebook's requirements:
 * - Remove all non-numeric characters and spaces
 * - Include country code (default to US +1 if not provided)
 * - Hash with SHA256
 */
export async function hashPhone(phone: string, countryCode: string = '1'): Promise<string | null> {
  if (!phone) return null;

  try {
    // Remove all non-numeric characters and spaces
    let normalized = phone.replace(/\s+/g, '').replace(/\D/g, '');

    // Add country code if not present (assuming US if no country code)
    if (!normalized.startsWith(countryCode)) {
      normalized = countryCode + normalized;
    }

    // Convert string to ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);

    // Hash using SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    console.error('Error hashing phone:', error);
    return null;
  }
}

/**
 * Hashes any string value using SHA256
 * Generic function for other PII fields (names, city, etc.)
 */
export async function hashString(value: string): Promise<string | null> {
  if (!value) return null;

  try {
    // Normalize: lowercase and remove all whitespace
    const normalized = value.toLowerCase().replace(/\s+/g, '');

    // Convert string to ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);

    // Hash using SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    console.error('Error hashing string:', error);
    return null;
  }
}

/**
 * Prepares user data for Snap Pixel with proper hashing
 * Returns both raw and hashed versions of PII
 */
export async function prepareSnapPixelUserData(userData: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}) {
  const [hashedEmail, hashedPhone] = await Promise.all([
    userData.email ? hashEmail(userData.email) : null,
    userData.phone ? hashPhone(userData.phone) : null,
  ]);

  return {
    // Raw data (Snap will hash if needed)
    user_email: userData.email || undefined,
    user_phone_number: userData.phone || undefined,
    firstname: userData.firstName || undefined,
    lastname: userData.lastName || undefined,
    geo_city: userData.city || undefined,
    geo_state: userData.state || undefined,
    geo_country: userData.country || undefined,
    geo_postal_code: userData.postalCode || undefined,

    // Pre-hashed data for privacy
    user_hashed_email: hashedEmail || undefined,
    user_hashed_phone_number: hashedPhone || undefined,
  };
}

/**
 * Format purchase data for Snap Pixel PURCHASE event
 */
export function formatSnapPurchaseData(sessionDetails: any, hashedUserData: any) {
  return {
    price: sessionDetails.amount,
    currency: sessionDetails.currency || 'USD',
    transaction_id: sessionDetails.order_id || sessionDetails.session_id,
    item_ids: [sessionDetails.plan_id || sessionDetails.price_id],
    item_category: 'subscription',
    number_items: 1,
    ...hashedUserData,  // Spread user data with hashed values
  };
}